import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { clerkEnabled } from "@/lib/clerk";

const AuthContext = createContext(null);

async function waitForClerk(maxMs = 5000) {
  const step = 100;
  for (let waited = 0; waited < maxMs; waited += step) {
    if (window.Clerk?.loaded) return true;
    await new Promise((r) => setTimeout(r, step));
  }
  return Boolean(window.Clerk?.loaded);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      if (clerkEnabled) {
        // Clerk mode: session is owned by Clerk; /auth/me resolves (and
        // auto-provisions) the local user from the verified token
        await waitForClerk();
        if (window.Clerk?.session) {
          try {
            const r = await api.get("/auth/me");
            setUser(r.data);
          } catch {
            /* token invalid — user stays null, routes redirect to /login */
          }
        }
        setLoading(false);
        return;
      }
      // Legacy mode
      const token = localStorage.getItem("isale_token");
      if (!token) {
        setLoading(false);
        return;
      }
      api
        .get("/auth/me")
        .then((r) => setUser(r.data))
        .catch(() => localStorage.removeItem("isale_token"))
        .finally(() => setLoading(false));
    };
    init();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("isale_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("isale_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const refreshUser = async () => {
    const r = await api.get("/auth/me");
    setUser(r.data);
    return r.data;
  };

  const logout = async () => {
    localStorage.removeItem("isale_token");
    setUser(null);
    if (clerkEnabled && window.Clerk) {
      await window.Clerk.signOut();
      return; // Clerk redirects via afterSignOutUrl
    }
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
